import { useState } from 'react'
import { Brand } from '../components/common/Brand'
import { SiteFooter } from '../components/common/SiteFooter'
import { PromiseSection } from '../components/waitlist/PromiseSection'
import { SignupFlow } from '../components/waitlist/SignupFlow'
import {
  countWords,
  MAX_WORDS,
  type FormField,
  type SignupStatus,
  type WaitlistFormData
} from '../components/waitlist/types'
import { useRuntimeEnvironment } from '../hooks/useRuntimeEnvironment'
import { supabase } from '../lib/supabase'

interface SignupResponse {
  submission_id: string
}

const initialFormData: WaitlistFormData = {
  email: '',
  user_profiles: '',
  leetcode_difficulty: [],
  codeforces_difficulty: [],
  lesson_time_ist: [],
  lesson_frequency: '',
  notes: ''
}

export function WaitlistPage() {
  const runtimeEnvironment = useRuntimeEnvironment()
  const [step, setStep] = useState(0)
  const [submissionId, setSubmissionId] = useState<string | null>(null)
  const [formData, setFormData] = useState<WaitlistFormData>(initialFormData)
  const [status, setStatus] = useState<SignupStatus>('idle')
  const [error, setError] = useState('')

  function updateValue(
    value: string,
    key: FormField,
    multi = false,
    isTextarea = false
  ) {
    if (isTextarea && countWords(value) > MAX_WORDS) {
      setError(`Please keep this answer within ${MAX_WORDS} words.`)
      return
    }
    setFormData((current) => {
      if (!multi) return { ...current, [key]: value }
      const selected = current[key] as string[]
      return {
        ...current,
        [key]: selected.includes(value)
          ? selected.filter((item) => item !== value)
          : [...selected, value]
      }
    })
    setError('')
  }

  async function saveEmail() {
    if (!formData.email.trim())
      return setError('Please enter your email to join the waitlist.')
    if (!supabase)
      return setError(
        'Waitlist signup is temporarily unavailable. Please try again later.'
      )
    setStatus('saving-email')
    setError('')
    const { data, error: saveError } =
      await supabase.functions.invoke<SignupResponse>('waitlist', {
        body: {
          action: 'signup',
          email: formData.email,
          environment: runtimeEnvironment
        }
      })
    if (saveError || !data?.submission_id) {
      setStatus('idle')
      setError(
        saveError?.code === '23505'
          ? 'That email is already on the list.'
          : 'Something went wrong. Please try again.'
      )
      return
    }
    setStatus('idle')
    setSubmissionId(data.submission_id)
    setStep(1)
  }

  async function submitWaitlist() {
    if (!supabase || !submissionId)
      return setError(
        'Your signup could not be verified. Please submit your email again.'
      )
    setStatus('submitting')
    setError('')
    const { error: submitError } = await supabase.functions.invoke('waitlist', {
      body: {
        action: 'preferences',
        submission_id: submissionId,
        user_profiles: formData.user_profiles || null,
        leetcode_difficulty: formData.leetcode_difficulty.length
          ? formData.leetcode_difficulty
          : null,
        codeforces_difficulty: formData.codeforces_difficulty.length
          ? formData.codeforces_difficulty
          : null,
        lesson_time_ist: formData.lesson_time_ist.length
          ? formData.lesson_time_ist
          : null,
        lesson_frequency: formData.lesson_frequency || null,
        notes: formData.notes || null
      }
    })
    if (submitError) {
      setStatus('idle')
      setError(
        submitError.code === '23505'
          ? 'Details for this submission were already saved.'
          : 'Something went wrong. Please try again.'
      )
      return
    }
    setStatus('success')
  }

  return (
    <main className="page-shell">
      <div className="grain" />
      <nav className="nav">
        <Brand />
        <div className="nav-links">
          <a className="pricing-link" href="/pricing">
            Pricing
          </a>
          <a
            className="youtube-link"
            href="https://www.youtube.com/@aryanc403?sub_confirmation=1"
            target="_blank"
            rel="noreferrer"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path
                className="youtube-logo-mark"
                d="M21.6 7.2a2.9 2.9 0 0 0-2-2C17.8 4.7 12 4.7 12 4.7s-5.8 0-7.6.5a2.9 2.9 0 0 0-2 2A30 30 0 0 0 2 12a30 30 0 0 0 .4 4.8 2.9 2.9 0 0 0 2 2c1.8.5 7.6.5 7.6.5s5.8 0 7.6-.5a2.9 2.9 0 0 0 2-2A30 30 0 0 0 22 12a30 30 0 0 0-.4-4.8Z"
              />
              <path
                className="youtube-logo-play"
                d="m10 15.4 4.8-3.4L10 8.6v6.8Z"
              />
            </svg>
            YouTube <span>Channel</span>
          </a>
        </div>
      </nav>
      <section className="hero" aria-labelledby="hero-title">
        <div className="tag">
          <span className="spark">✦</span> Coming soon
        </div>
        <h1 id="hero-title">
          I hate <em>CP?</em>
          <br />
          Say no more.
        </h1>
        <p className="intro">
          A better way to learn, practice, and improve at competitive
          programming.
        </p>
        <p className="learner-trust">
          <span aria-hidden="true">✦</span> Trusted by 17K+ learners
        </p>
        <SignupFlow
          step={step}
          status={status}
          formData={formData}
          error={error}
          onBack={() => setStep(0)}
          onSaveEmail={saveEmail}
          onSubmit={submitWaitlist}
          onChange={updateValue}
        />
      </section>
      <PromiseSection />
      <SiteFooter />
    </main>
  )
}
